import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

/**
 * Generic hook to manage content modal state and determine context
 *
 * Context detection:
 * - Modal behavior: When NOT on content pages and NOT from home menu
 * - Navigation behavior: When on content pages or from home menu
 *
 * @param {string} contentType - The type of content ('calculator', 'resource', etc.)
 */
const useContentModal = (contentType = "content") => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    contentId: null,
  });

  const location = useLocation();

  /**
   * Determines if content should open in modal or navigate
   * Based on current location context
   */
  const shouldUseModal = useCallback(() => {
    const currentPath = location.pathname;

    // If we're already on content pages of this type, use navigation (existing behavior)
    if (currentPath.startsWith(`/${contentType}`)) {
      return false;
    }

    // If we're on the home page and will implement menu there, use navigation
    // TODO: Add home page detection when implemented
    // if (currentPath === '/' || currentPath === '/home') {
    //   return false;
    // }

    // For all other contexts, use modal
    return true;
  }, [location.pathname, contentType]);

  /**
   * Opens content in modal or navigates based on context
   */
  const openContent = useCallback(
    (contentId, navigate) => {
      if (shouldUseModal()) {
        // Open in modal
        setModalState({
          isOpen: true,
          contentId: contentId,
        });
      } else {
        // Navigate to content page (existing behavior)
        navigate(`/${contentType}/${contentId}`);
      }
    },
    [shouldUseModal, contentType],
  );

  /**
   * Closes the content modal
   */
  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      contentId: null,
    });
  }, []);

  return {
    modalState,
    shouldUseModal: shouldUseModal(),
    openContent,
    closeModal,
  };
};

export default useContentModal;
